<template>
  <div ref="host" class="shadow-html-host" :title="titleText"></div>
</template>

<script>
export default {
  props: {
    html: {
      type: String,
      default: '',
    },
    clampLines: {
      type: Number,
      default: 0,
    },
    previewTitle: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      shadowRootRef: null,
    };
  },
  computed: {
    isPreviewMode() {
      return Number(this.clampLines) > 0;
    },
    isPostDetailMode() {
      return this.$refs.host?.classList?.contains('post-description');
    },
    isReplyDetailMode() {
      return this.$refs.host?.classList?.contains('reply-description');
    },
    normalizedHtml() {
      return this.html || '-';
    },
    titleText() {
      return this.previewTitle || this.extractPlainText(this.normalizedHtml);
    },
  },
  mounted() {
    this.renderShadow();
  },
  watch: {
    normalizedHtml() {
      this.renderShadow();
    },
    clampLines() {
      this.renderShadow();
    },
    previewTitle() {
      this.renderShadow();
    },
  },
  methods: {
    extractPlainText(html) {
      if (!html) return '';
      const container = document.createElement('div');
      container.innerHTML = String(html)
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/(p|div|li|h1|h2|h3|h4|h5|h6)>/gi, '\n');
      return (container.textContent || container.innerText || '')
        .replace(/\u00a0/g, ' ')
        .replace(/\s*\n\s*/g, ' ')
        .replace(/\s{2,}/g, ' ')
        .trim();
    },
    getShadowStyles() {
      const detailTextStyles = this.isPostDetailMode
        ? `
        .content {
          color: #0F1724;
          font-family: 'Inter', 'Segoe UI', sans-serif;
          font-size: 16px;
          line-height: 22px;
          font-weight: 700;
        }
        
        .content p,
        .content div,
        .content ul,
        .content ol,
        .content blockquote {
          margin: 0 0 6px;
        }
      `
        : this.isReplyDetailMode
          ? `
        .content {
          color: #111827;
          font-family: 'Inter', 'Segoe UI', sans-serif;
          font-size: 15px;
          line-height: 22px;
          font-weight: 400;
        }
       
      `
          : '';

      const baseStyles = `
        :host {
          display: block;
        }
        .content {
          color: #2f3b52;
          font-family: Arial, sans-serif;
          font-size: 14px;
          line-height: 1.45;
          white-space: normal;
          word-break: break-word;
        }
        .content * {
          box-sizing: border-box;
        }
        .content h1,
        .content h2,
        .content h3,
        .content h4,
        .content h5,
        .content h6 {
          color: #1f2a44;
          font-weight: 700;
          line-height: 1.15;
          margin: 0 0 4px;
        }
       
        .content p,
        .content div,
        .content ul,
        .content ol,
        .content blockquote {
          margin: 0 0 6px;
        }
        .content ul,
        .content ol {
          padding-left: 20px;
        }
        .content li + li {
          margin-top: 6px;
        }
        .content a {
          color: #2f6fd6;
          text-decoration: underline;
        }
        .content > *:last-child {
          margin-bottom: 0;
        }
        .content img,
        .content video,
        .content iframe,
        .content table {
          max-width: 100%;
        }
        ${detailTextStyles}
      `;

      if (!this.isPreviewMode) {
        return baseStyles;
      }

      return `
        ${baseStyles}
        .content {
          position: relative;
          overflow: hidden;
          max-height: calc(1.45em * ${Number(this.clampLines)});
          padding-right: 16px;
        }
        .content::after {
          content: '...';
          position: absolute;
          right: 0;
          bottom: 0;
          padding-left: 10px;
          background: linear-gradient(to right, rgba(244, 247, 251, 0), rgba(244, 247, 251, 1) 50%);
          color: #2f3b52;
          display: none;
        }
        .content.is-overflowing::after {
          display: block;
        }
        // .content h1 { font-size: 18px; }
        // .content h2 { font-size: 16px; }
        .content h3,
        .content h4,
        .content h5,
        .content h6 {
          font-size: 14px;
          line-height: 1.45;
          margin: 0;
        }
        .content p,
        .content div,
        .content ul,
        .content ol,
        .content blockquote {
          margin: 0;
        }
        .content li + li {
          margin-top: 0;
        }
        .content > * {
          max-width: 100%;
        }
        .content img,
        .content video,
        .content iframe,
        .content table {
          display: none;
        }
      `;
    },
    renderShadow() {
      if (!this.$refs.host) {
        return;
      }
      if (!this.shadowRootRef) {
        this.shadowRootRef = this.$refs.host.attachShadow({ mode: 'open' });
      }
      this.shadowRootRef.innerHTML = `
        <style>${this.getShadowStyles()}</style>
        <div class="content">${this.normalizedHtml}</div>
      `;
      if (this.isPreviewMode) {
        const contentEl = this.shadowRootRef.querySelector('.content');
        if (contentEl) {
          const isOverflowing = contentEl.scrollHeight > contentEl.clientHeight + 1;
          contentEl.classList.toggle('is-overflowing', isOverflowing);
        }
      }
    },
  },
};
</script>

<style scoped>
.shadow-html-host {
  display: block;
  width: 100%;
}
</style>
